import { Heading, Link, Modal, Paragraph, Popover } from 'component-library';
import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useFlag } from '#lib/hooks/useFlag';
import { routePathsAdmin } from '../../routePaths/adminRoutePaths';
import { usePersistedEntitiesContext } from '../../PersistedEntities/PersistedEntitiesContext';
import { useLocations } from '../../Locations';
import { CountIconWrapper, NotificationIconWrapper, NotificationImageWrapper } from './styles';

const SeenNotificationPilotInfoKey = 'seen-notification-pilot-info';

type NotificationPilotInfoProps = {
  userMenuOpen?: boolean;
};

export const NotificationPilotInfo = ({ userMenuOpen }: NotificationPilotInfoProps) => {
  const showInfoFlag = useFlag('notifications-extended-pilot');

  const showModalFlag = useFlag('notifications-extended-pilot-modal');

  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const { userHasNBPPilot } = useLocations();

  const navigate = useNavigate();

  const { setEntity, getEntity, loading, error } = usePersistedEntitiesContext();

  const showInfo = showInfoFlag && isOpen && userHasNBPPilot;

  const closeInfo = useCallback(() => {
    if (!showInfo) return;

    setIsOpen(false);

    void setEntity(SeenNotificationPilotInfoKey, true);
  }, [setEntity, showInfo]);

  useEffect(() => {
    if (loading || error) {
      setIsOpen(false);

      return;
    }
    const entity = getEntity<boolean>(SeenNotificationPilotInfoKey);

    if (entity && entity.length > 0) {
      setIsOpen(!entity[0].value);
    } else {
      setIsOpen(true);
    }
  }, [getEntity, loading, error]);

  useEffect(() => {
    if (userMenuOpen) {
      closeInfo();
    }
  }, [closeInfo, userMenuOpen]);

  const handleLinkClick = () => {
    closeInfo();
    void navigate(routePathsAdmin.PROFILE_NOTIFICATION);
  };

  if (!showInfoFlag || !userHasNBPPilot) {
    return null;
  }

  if (showModalFlag) {
    return (
      <Modal
        open={showInfo}
        onClose={closeInfo}
        closeOnClickOutside={false}
        secondaryAction={{
          label: t('button.close'),
          action: closeInfo,
        }}
        maxWidth="610px"
        minWidth="610px"
      >
        <NotificationImageWrapper>
          <NotificationIconWrapper>
            <NotificationIcon />
            <CountIconWrapper>1</CountIconWrapper>
          </NotificationIconWrapper>
        </NotificationImageWrapper>
        <Heading as="h2" variant="large" mb="xsmall">
          {t('notification.pilot.info.modal.title')}
        </Heading>
        <Paragraph>
          <Trans
            i18nKey="notification.pilot.info.modal.description"
            components={{
              ProfileLink: (
                <Link
                  inline
                  href={routePathsAdmin.PROFILE_NOTIFICATION}
                  onClick={handleLinkClick}
                />
              ),
            }}
          />
        </Paragraph>
      </Modal>
    );
  } else {
    return (
      <Popover
        elementId="user-profile-icon"
        show={showInfo}
        placement="bottom-end"
        onClose={closeInfo}
      >
        <Trans
          i18nKey="header.notification.pilot.info"
          components={{
            Link: <Link inline={true} onClick={handleLinkClick} href="#" />,
          }}
        />
      </Popover>
    );
  }
};

export const NotificationIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="112"
      height="112"
      viewBox="0 0 112 112"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M78.805 21.0054C79.1817 21.2888 79.4033 21.7327 79.4033 22.2041V77.1028C79.4033 77.5742 79.1817 78.0181 78.805 78.3015C78.4283 78.5849 77.9403 78.6747 77.4874 78.544L29.3397 64.6482L12.5482 63.2481C11.7708 63.1833 11.1729 62.5334 11.1729 61.7533V37.5635C11.1729 36.7837 11.7703 36.134 12.5473 36.0688L29.3392 34.6588L77.4874 20.7629C77.9403 20.6322 78.4283 20.722 78.805 21.0054ZM76.4033 24.1982L30.0286 37.5823C29.9337 37.6097 29.8365 37.6276 29.7381 37.6359L14.1729 38.9428V60.3731L29.7373 61.6709C29.8359 61.6792 29.9335 61.6971 30.0286 61.7246L76.4033 75.1086V24.1982Z"
        fill="#657B6C"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M29.0195 35.9551C29.848 35.9551 30.5195 36.6267 30.5195 37.4551V61.8523C30.5195 62.6807 29.848 63.3523 29.0195 63.3523C28.1911 63.3523 27.5195 62.6807 27.5195 61.8523V37.4551C27.5195 36.6267 28.1911 35.9551 29.0195 35.9551Z"
        fill="#657B6C"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.64355 43.708C4.64355 42.8796 5.31513 42.208 6.14355 42.208H11.9712C12.7997 42.208 13.4712 42.8796 13.4712 43.708C13.4712 44.5364 12.7997 45.208 11.9712 45.208H7.64355V54.1103H12.1391C12.9676 54.1103 13.6391 54.7819 13.6391 55.6103C13.6391 56.4387 12.9676 57.1103 12.1391 57.1103H6.14355C5.31513 57.1103 4.64355 56.4387 4.64355 55.6103V43.708Z"
        fill="#657B6C"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M76.8672 41.6533C76.8672 40.8249 77.5388 40.1533 78.3672 40.1533C83.6207 40.1533 87.8778 44.4104 87.8778 49.6639C87.8778 54.9174 83.6207 59.1745 78.3672 59.1745C77.5388 59.1745 76.8672 58.5029 76.8672 57.6745C76.8672 56.8461 77.5388 56.1745 78.3672 56.1745C81.9638 56.1745 84.8778 53.2606 84.8778 49.6639C84.8778 46.0672 81.9638 43.1533 78.3672 43.1533C77.5388 43.1533 76.8672 42.4817 76.8672 41.6533Z"
        fill="#657B6C"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M90.7646 49.6533C90.7646 48.8249 91.4362 48.1533 92.2646 48.1533H105.846C106.675 48.1533 107.346 48.8249 107.346 49.6533C107.346 50.4817 106.675 51.1533 105.846 51.1533H92.2646C91.4362 51.1533 90.7646 50.4817 90.7646 49.6533Z"
        fill="#657B6C"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M106.151 36.0006C106.474 36.7632 106.118 37.6437 105.356 37.9672L92.8508 43.2714C92.0881 43.5949 91.2076 43.2389 90.8841 42.4762C90.5606 41.7136 90.9166 40.8331 91.6793 40.5096L104.184 35.2054C104.947 34.8819 105.827 35.2379 106.151 36.0006Z"
        fill="#657B6C"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M90.884 56.8316C91.2075 56.069 92.088 55.713 92.8507 56.0365L105.355 61.3406C106.118 61.6641 106.474 62.5446 106.151 63.3073C105.827 64.0699 104.947 64.426 104.184 64.1025L91.6792 58.7983C90.9165 58.4748 90.5605 57.5943 90.884 56.8316Z"
        fill="#657B6C"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.9428 61.2576C19.7188 60.9674 20.5831 61.3611 20.8733 62.137L29.6148 85.507L29.615 85.5073C30.4215 87.6654 32.827 88.7623 34.9842 87.9561C37.1422 87.1496 38.2391 84.7441 37.4329 82.5869L30.6373 64.4127C30.3472 63.6367 30.741 62.7725 31.517 62.4823C32.2929 62.1922 33.1572 62.586 33.4473 63.362L40.243 81.5364L40.243 81.5366C41.6296 85.2466 39.7436 89.38 36.0344 90.7662C32.3245 92.1528 28.1913 90.2669 26.8049 86.558C26.8049 86.5579 26.8048 86.5577 26.8048 86.5576L18.0634 63.188C17.7732 62.4121 18.1669 61.5478 18.9428 61.2576Z"
        fill="#657B6C"
      />
    </svg>
  );
};
